import React from 'react'
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Paper,
  Popper,
  Fade,
  Tooltip
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { removeIndianNumberFormatting } from "../../constants/index.js"
const valueDisabledOperators = ['IS_NULL', 'IS_NOT_NULL']

const TruncatedValue = ({ value }) => (
  <Tooltip title={value || ''} arrow>
    <Box
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        cursor: 'pointer',
      }}
    >
      {value}
    </Box>
  </Tooltip>
);

// Operator display and payload mapping
const operatorOptions = [
  { label: 'EQUAL TO', value: 'EQUAL_TO' },
  { label: 'NOT EQUAL TO', value: 'NOT_EQUAL_TO' },
  { label: 'CONTAINS', value: 'LIKE' },
  { label: 'IS NULL', value: 'IS_NULL' },
  { label: 'IS NOT NULL', value: 'IS_NOT_NULL' },
  { label: 'IN LIST (Exact word)', value: 'IN_LIST' },
  { label: 'NOT IN LIST (Exact word)', value: 'NOT_IN_LIST' },
  { label: 'STARTS WITH', value: 'STARTS_WITH' },
  { label: 'ENDS WITH', value: 'ENDS_WITH' },
  {
    label: 'IN LIST (Contains, probable match)',
    value: 'IN_LIST',
  },
  {
    label: 'NOT IN LIST (Contains, probable match)',
    value: 'NOT_IN_LIST',
  },
]

// Restricted operator options for dropdown fields
const dropdownOperatorOptions = [
  { label: 'EQUAL TO', value: 'EQUAL_TO' },
  { label: 'NOT EQUAL TO', value: 'NOT_EQUAL_TO' },
  { label: 'IS NULL', value: 'IS_NULL' },
  { label: 'IS NOT NULL', value: 'IS_NOT_NULL' },
]

// Get placeholder and warning based on selected field and operator (move outside component)
const getPlaceholderAndWarning = (fields, fieldId, operator) => {
  const field = fields.find(f => f.id === fieldId)
  let placeholder = field?.placeholder || `Enter ${field?.label || 'value'}`
  let warning = ''
  switch (operator) {
    case 'IN LIST (Exact word)':
    case 'NOT IN LIST (Exact word)':
    case 'IN LIST (Contains, probable match)':
    case 'NOT IN LIST (Contains, probable match)':
      placeholder = 'Enter comma separated value'
      break
    default:
    // no-op
  }
  return { placeholder, warning }
}

const Search = ({
  open,
  anchorEl,
  onClose,
  onApply,
  onReset,
  fields = [],
  operator = 'EQUAL_TO',
  placement = 'bottom-start',
  searchCriteria = [
    { id: 1, logicalOperator: 'Where', field: fields[0]?.id || '', value: '' },
  ],
  setSearchCriteria,
}) => {
  // Map filterOperator to logicalOperator for UI state
  const mapFilterOperatorToLogical = op => {
    if (!op) return ''
    if (op === 'AND') return 'And'
    if (op === 'OR') return 'OR'
    if (op === 'WHERE') return 'Where'
    return op.charAt(0) + op.slice(1).toLowerCase()
  }

  // Handle field change
  const handleFieldChange = (id, key, value) => {
    setSearchCriteria(prev =>
      prev.map(criteria => {
        if (criteria.id === id) {
          if (key === 'field') {
            // When field changes, reset value and operator to default
            const fieldConfig = fields.find(f => f.id === value) || {}
            const defaultOperator = fieldConfig.type === 'dropdown' ? dropdownOperatorOptions[0].value : operatorOptions[0].value
            return { ...criteria, field: value, value: '', operator: defaultOperator }
          }
          if (key === 'operator') {
            // When operator changes, reset value to empty
            return { ...criteria, operator: value, value: '' }
          }
          // Special handling: if value is being set and operator is a list type, always store as comma-separated string
          if (key === 'value') {
            const op = criteria.operator || operatorOptions[0].value;
            if ([
              'IN_LIST',
              'NOT_IN_LIST',
              'IN_LIST_PROBABLE_MATCH',
              'NOT_IN_LIST_PROBABLE_MATCH',
            ].includes(op) && Array.isArray(value)) {
              return { ...criteria, value: value.join(', ') };
            }
          }
          return { ...criteria, [key]: value }
        }
        return criteria
      })
    )
  }

  // When loading criteria from BE, map filterOperator to logicalOperator for UI
  React.useEffect(() => {
    // If any criteria has filterOperator, map it to logicalOperator of the PREVIOUS row (for UI)
    if (searchCriteria.some(c => c.filterOperator)) {
      setSearchCriteria(prev => {
        return prev.map((c, idx, arr) => {
          if (idx === 0) {
            // If first item has filterOperator, use it for the second item's logicalOperator
            return { ...c, logicalOperator: 'Where', filterOperator: undefined }
          }
          // For others, use filterOperator from previous item
          const prevFilterOp = arr[idx - 1]?.filterOperator
          if (prevFilterOp) {
            return {
              ...c,
              logicalOperator: mapFilterOperatorToLogical(prevFilterOp),
              filterOperator: undefined,
            }
          } else {
            // If no filterOperator, default to 'And' for UI
            return { ...c, logicalOperator: 'And' }
          }
        })
      })
    }
  }, [searchCriteria, setSearchCriteria])

  // Add new search criteria
  const handleAddCriteria = () => {
    const newId =
      searchCriteria.length > 0
        ? Math.max(...searchCriteria.map(c => c.id)) + 1
        : 1
    setSearchCriteria([
      ...searchCriteria,
      {
        id: newId,
        logicalOperator: 'And',
        field: fields[0]?.id || '',
        value: '',
      },
    ])
  }

  // Remove search criteria
  const handleRemoveCriteria = id => {
    if (searchCriteria.length > 1) {
      const filteredCriteria = searchCriteria.filter(
        criteria => criteria.id !== id
      )
      // If we're removing the first item and it was "Where", make the next item "Where"
      if (filteredCriteria.length > 0 && searchCriteria[0].id === id) {
        filteredCriteria[0].logicalOperator = 'Where'
      }
      setSearchCriteria(filteredCriteria)
    }
  }
  React.useEffect(() => {
    if (!open) return; // Only update when popper opens
    const updated = searchCriteria.map(criteria => {
      const op = criteria.operator;
      if (
        ['IN_LIST', 'NOT_IN_LIST', 'IN_LIST_PROBABLE_MATCH', 'NOT_IN_LIST_PROBABLE_MATCH'].includes(op) &&
        Array.isArray(criteria.searchValueList)
      ) {
        const joined = criteria.searchValueList.join(', ');
        if (criteria.value !== joined) {
          return { ...criteria, value: joined };
        }
      }
      return criteria;
    });

    // Only call setState if something changed
    const changed = updated.some((c, i) => c.value !== searchCriteria[i].value);
    if (changed) {
      setSearchCriteria(updated);
    }
  }, [open]); // <-- only run when search popper opens

  // Apply search
  const handleApply = () => {
    const filtered = searchCriteria.filter(criteria => {
      const op = criteria.operator;

      if (['IS_NULL', 'IS_NOT_NULL'].includes(op)) return true;

      if (criteria?.value !== undefined && criteria?.value !== null) {
        if (typeof criteria.value === "string") {
          return criteria.value.trim() !== "";
        }
        if (typeof criteria.value === "boolean") {
          return true;
        }
        return true;
      }

      return false;
    });

    const formattedCriteria = filtered.map((criteria, idx) => {
      const op = criteria.operator || operator;

      const result = { fieldName: criteria.field, operator: op };

      if (
        ['IN_LIST', 'NOT_IN_LIST', 'IN_LIST_PROBABLE_MATCH', 'NOT_IN_LIST_PROBABLE_MATCH'].includes(op)
      ) {
        result.searchValueList = criteria.value
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      } else if (!['IS_NULL', 'IS_NOT_NULL'].includes(op)) {
        result.value = removeIndianNumberFormatting(criteria.value);
      }

      if (idx < filtered.length - 1) {
        const nextLogical = filtered[idx + 1].logicalOperator;
        if (nextLogical) result.filterOperator = nextLogical.toUpperCase();
      }

      return result;
    });

    onApply(formattedCriteria);
  };

  // Reset search
  const handleReset = () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: fields[0]?.id || '',
        value: '',
      },
    ])
    onReset()
  }

  // Determine if value input should be disabled for a criteria
  const isValueDisabled = criteria => {
    const op = criteria.operator || operatorOptions[0].value
    return valueDisabledOperators.includes(op)
  }

  const hasErrors = searchCriteria.some(criteria => {
    const fieldConfig = fields.find(f => f.id === criteria.field);

    // Skip value requirement for IS_NULL / IS_NOT_NULL
    const op = criteria.operator;
    const valueRequired = !['IS_NULL', 'IS_NOT_NULL'].includes(op);

    // 1️⃣ Missing required value
    if (valueRequired && (criteria.value === '' || criteria.value === null || criteria.value === undefined)) {
      return true;
    }

    // 2️⃣ Zod validation error
    if (fieldConfig?.zod && criteria.value) {
      const result = fieldConfig.zod.safeParse(criteria.value);
      if (!result.success) return true;
    }

    return false;
  });


  // Determine if Apply button should be disabled
  const isApplyDisabled = hasErrors;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            altBoundary: true,
            rootBoundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      sx={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper
            elevation={0}
            sx={{
              mt: 1,
              width: {
                xs: '95vw',
                sm: '90vw',
                md: '850px',
                lg: '900px',
              },
              maxWidth: '1000px',
              minWidth: '700px',
              borderRadius: '12px',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              bgcolor: '#FFFFFF',
            }}
          >
            <Box sx={{ p: 3 }}>
              {/* Search Criteria Rows */}
              <Box>
                {searchCriteria.map((criteria, index) => (
                  <Box
                    key={criteria.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      gap: 2,
                      minHeight: '48px',
                    }}
                  >
                    {/* Logical Operator (Where/And/OR) */}
                    <Box sx={{ minWidth: '80px' }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={criteria.logicalOperator}
                          onChange={e =>
                            handleFieldChange(
                              criteria.id,
                              'logicalOperator',
                              e.target.value
                            )
                          }
                          disabled={index === 0}
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            height: '48px',
                            bgcolor: 'transparent',
                            fontSize: '14px',
                            fontWeight: 400,
                            color: '#374151',
                            mt: '16px',
                            border: 'none',
                            '& .MuiOutlinedInput-notchedOutline': {
                              border: 'none',
                            },
                            '& .MuiSelect-select': {
                              padding: '12px 32px 12px 0px',
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--forbrand-color)',
                              fontSize: '20px',
                              right: '8px',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 200,
                                zIndex: 1400,
                                maxWidth: 300,
                                borderRadius: '8px',
                                '& .MuiMenuItem-root': {
                                  fontSize: '14px',
                                  padding: '8px 16px',
                                },
                              },
                            },
                          }}
                        >
                          {index === 0
                            ? [<MenuItem key="Where" value="Where" sx={{ fontSize: '14px' }}>Where</MenuItem>]
                            : [
                              <MenuItem key="And" value="And" sx={{ fontSize: '14px' }}>And</MenuItem>,
                              <MenuItem key="OR" value="OR" sx={{ fontSize: '14px' }}>OR</MenuItem>
                            ]
                          }
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Field Selector */}
                    <Box sx={{ flex: '1', minWidth: '160px' }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={criteria.field}
                          onChange={e =>
                            handleFieldChange(
                              criteria.id,
                              'field',
                              e.target.value
                            )
                          }
                          IconComponent={KeyboardArrowDownIcon}
                          sx={{
                            height: '48px',
                            bgcolor: '#fff',
                            fontSize: '14px',
                            color: '#374151',
                            borderRadius: 'unset',
                            px: 2,
                            mt: '16px',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--forbrand-color)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--forbrand-color)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'var(--forbrand-color)',
                              borderWidth: '1px',
                            },
                            '& .MuiSelect-select': {
                              padding: '12px 32px 12px 0px',
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '& .MuiSvgIcon-root': {
                              color: 'var(--forbrand-color)',
                              fontSize: '20px',
                              right: '8px',
                            },
                          }}
                          renderValue={selectedId => {
                            const selectedOption = fields.find(f => f.id === selectedId);
                            const value = selectedOption ? selectedOption.label : '';
                            return <TruncatedValue value={value} />;
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                zIndex: 1400,
                                maxWidth: 300,
                                borderRadius: '8px',
                                '& .MuiMenuItem-root': {
                                  fontSize: '14px',
                                  padding: '8px 16px',
                                },
                              },
                            },
                          }}
                        >
                          {fields.map(option => (
                            <MenuItem
                              key={option.id}
                              value={option.id}
                              sx={{ fontSize: '14px' }}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Condition Operator */}
                    <Box sx={{ flex: '1', minWidth: '160px' }}>
                      <FormControl fullWidth size="small">
                        {(() => {
                          const fieldConfig =
                            fields.find(f => f.id === criteria.field) || {}
                          const optionsToShow =
                            fieldConfig.type === 'dropdown'
                              ? dropdownOperatorOptions
                              : operatorOptions
                          return (
                            <Select
                              value={
                                criteria.operator || optionsToShow[0].value
                              }
                              onChange={e =>
                                handleFieldChange(
                                  criteria.id,
                                  'operator',
                                  e.target.value
                                )
                              }
                              IconComponent={KeyboardArrowDownIcon}
                              sx={{
                                height: '48px',
                                bgcolor: '#fff',
                                fontSize: '14px',
                                color: '#374151',
                                borderRadius: 'unset',
                                px: 2,
                                mt: '16px',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--forbrand-color)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--forbrand-color)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'var(--forbrand-color)',
                                  borderWidth: '1px',
                                },
                                '& .MuiSelect-select': {
                                  padding: '12px 32px 12px 0px',
                                  display: 'flex',
                                  alignItems: 'center',
                                },
                                '& .MuiSvgIcon-root': {
                                  color: 'var(--forbrand-color)',
                                  fontSize: '20px',
                                  right: '8px',
                                },
                              }}
                              renderValue={selectedValue => {
                                const allOptions = [...operatorOptions, ...dropdownOperatorOptions];
                                const option = allOptions.find(o => o.value === selectedValue);
                                const label = option ? option.label : selectedValue || '';
                                return <TruncatedValue value={label} />;
                              }}

                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 300,
                                    zIndex: 1400,
                                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid #E5E7EB',
                                    px: 2,
                                    borderRadius: '8px',
                                    '& .MuiMenuItem-root': {
                                      fontSize: '14px',
                                      padding: '8px 16px',
                                    },
                                  },
                                },
                              }}
                            >
                              {optionsToShow.map(option => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                  sx={{ fontSize: '14px' }}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          )
                        })()}
                      </FormControl>
                    </Box>

                    {/* Operator is constant, so no dropdown */}

                    {/* Value Input - dynamic by type */}
                    <Box sx={{ flex: '1', minWidth: '160px' }}>
                      {(() => {
                        const fieldConfig =
                          fields.find(f => f.id === criteria.field) || {}

                        let error = ''
                        if (fieldConfig.zod && criteria.value) {
                          const result = fieldConfig.zod.safeParse(
                            criteria.value
                          )
                          if (!result.success) {
                            error =
                              result.error.issues[0]?.message || 'Invalid Input'
                          }
                        }
                        // Get placeholder and warning for this operator
                        const { placeholder, warning } =
                          getPlaceholderAndWarning(
                            fields,
                            criteria.field,
                            criteria.operator || operatorOptions[0].value
                          )
                        const disabled = isValueDisabled(criteria)
                        // Show dropdown for type 'dropdown', text field for 'text', date picker for 'date'
                        if (fieldConfig.type === 'date') {
                          return (
                            <TextField
                              fullWidth
                              size="small"
                              type="date"
                              value={criteria.value}
                              onChange={e =>
                                handleFieldChange(
                                  criteria.id,
                                  'value',
                                  e.target.value
                                )
                              }
                              placeholder={placeholder}
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              error={!!error}
                              helperText={error || warning}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '48px',
                                  fontSize: '14px',
                                  backgroundColor: disabled ? '#f5f5f5' : '#fff',
                                  color: '#374151',
                                  borderRadius: 'unset',
                                  px: 2,
                                  '& fieldset': {
                                    borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: error ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                                    borderWidth: '1px',
                                  },
                                  '& input': {
                                    padding: '12px 0px',
                                    fontSize: '14px',
                                    '&::placeholder': {
                                      fontSize: '14px',
                                      color: '#9CA3AF',
                                    },
                                  },
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                  backgroundColor: '#f5f5f5',
                                },
                              }}
                              disabled={disabled}
                            />
                          )
                        } else if (fieldConfig.type === 'dropdown' && Array.isArray(fieldConfig.options) && fieldConfig.options.length > 0) {
                          return (
                            <FormControl fullWidth size="small">
                              <Select
                                value={
                                  // Normalize the value to match the type in MenuItem
                                  fieldConfig.options.find(opt => String(opt.value) === String(criteria.value))
                                    ?.value ?? ''
                                }
                                onChange={e => {
                                  let val = e.target.value;
                                  // Convert string "true"/"false" to boolean if needed
                                  if (val === 'true') val = true;
                                  if (val === 'false') val = false;
                                  handleFieldChange(criteria.id, 'value', val);
                                }}
                                displayEmpty
                                IconComponent={KeyboardArrowDownIcon}
                                renderValue={selected => {
                                  if (selected === '' || selected === null || selected === undefined) return placeholder;
                                  // Find the label from options dynamically
                                  const matchedOption = fieldConfig.options.find(
                                    opt => String(opt.value) === String(selected)
                                  );
                                  return (
                                    <Tooltip title={matchedOption?.label || placeholder} arrow>
                                      <Box
                                        sx={{
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {matchedOption?.label || placeholder}
                                      </Box>
                                    </Tooltip>
                                  );
                                }}
                                sx={{
                                  height: '48px',
                                  bgcolor: disabled ? '#f5f5f5' : '#fff',
                                  fontSize: '14px',
                                  color: '#374151',
                                  borderRadius: 'unset',
                                  px: 2,
                                  mt: '16px',
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--forbrand-color)',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--forbrand-color)',
                                  },
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--forbrand-color)',
                                    borderWidth: '1px',
                                  },
                                  '& .MuiSelect-select': {
                                    padding: '12px 32px 12px 0px',
                                    display: 'flex',
                                    alignItems: 'center',
                                  },
                                  '& .MuiSvgIcon-root': {
                                    color: 'var(--forbrand-color)',
                                    fontSize: '20px',
                                    right: '8px',
                                  },
                                  '&.Mui-disabled': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                                disabled={disabled}
                                MenuProps={{
                                  PaperProps: {
                                    sx: {
                                      maxHeight: 200,
                                      zIndex: 1400,
                                      maxWidth: 300,
                                      borderRadius: '8px',
                                      '& .MuiMenuItem-root': {
                                        fontSize: '14px',
                                        padding: '8px 16px',
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="" disabled>
                                  {placeholder}
                                </MenuItem>
                                {fieldConfig.options.map(opt => (
                                  <MenuItem key={opt.value ?? opt.label} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>

                              {warning && (
                                <Box
                                  sx={{
                                    color: 'var(--forbrand-color)',
                                    fontSize: '12px',
                                    mt: 0.5,
                                  }}
                                >
                                  {warning}
                                </Box>
                              )}
                            </FormControl>

                          )
                        } else {
                          // Default: show text field
                          return (
                            <>
                              <TextField
                                fullWidth
                                size="small"
                                value={criteria.value}
                                onChange={e =>
                                  handleFieldChange(
                                    criteria.id,
                                    'value',
                                    e.target.value
                                  )
                                }
                                placeholder={placeholder}
                                variant="outlined"
                                error={!!error}
                                helperText={error || warning}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    height: '48px',
                                    bgcolor: disabled ? '#f5f5f5' : '#fff',
                                    fontSize: '14px',
                                    color: '#374151',
                                    borderRadius: 'unset',
                                    px: 2,
                                    mt: '16px',
                                    '& fieldset': {
                                      borderColor: error ? 'var(--forbrand-error)' : '#d0d0d0',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: error ? 'var(--forbrand-error)' : '#a0a0a0',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: error ? 'var(--forbrand-error)' : 'var(--forbrand-color)',
                                      borderWidth: '1px',
                                    },
                                    '& input': {
                                      padding: '12px 0px',
                                      fontSize: '14px',
                                      '&::placeholder': {
                                        fontSize: '14px',
                                        color: '#9CA3AF',
                                      },
                                    },
                                  },
                                  '& .MuiOutlinedInput-root.Mui-disabled': {
                                    backgroundColor: '#f5f5f5',
                                  },
                                }}
                                disabled={disabled}
                              />
                              {warning && !error && (
                                <Box sx={{ color: 'red', fontSize: '12px' }}>
                                  {warning}
                                </Box>
                              )}
                            </>
                          )
                        }
                      })()}
                    </Box>

                    {/* Delete Button */}
                    <Box sx={{ flex: '0 0 auto', ml: 1 }}>
                      {searchCriteria.length > 1 && (
                        <IconButton
                          onClick={() => handleRemoveCriteria(criteria.id)}
                          size="small"
                          sx={{
                            color: 'var(--forbrand-color)',
                            borderRadius: '4px',
                            width: '32px',
                            height: '32px',
                            '&:hover': {
                              backgroundColor: 'rgba(220, 38, 38, 0.1)',
                            },
                          }}
                        >
                          <DeleteOutlineIcon
                            color="var(--forbrand-error)"
                            sx={{ fontSize: '18px' }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Add Filter Button */}
              <Box>
                <Button
                  onClick={handleAddCriteria}
                  startIcon={<AddIcon color="var(--forbrand-color)" />}
                  sx={{
                    color: 'var(--forbrand-color)',
                    fontSize: '14px',
                    fontWeight: 400,
                    textTransform: 'none',
                    padding: '8px 0px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                    },
                    '& .MuiButton-startIcon': {
                      marginRight: '8px',
                      '& svg': {
                        fontSize: '16px',
                      },
                    },
                  }}
                >
                  Add Filter
                </Button>
              </Box>

              {/* Action Buttons */}

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  onClick={handleReset}
                  sx={{
                    color: 'var(--forbrand-bg-variant)',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '16px',
                    padding: '8px 16px',
                  }}
                >
                  Reset
                </Button>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    color: 'var(--forbrand-primary)',
                    borderColor: 'var(--border-strong)',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '14px',
                    padding: '8px 16px',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApply}
                  variant="contained"
                  sx={{
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                    fontWeight: 500,
                    textTransform: 'none',
                    fontSize: '14px',
                    padding: '8px 16px',
                    boxShadow: 'none',
                  }}
                  disabled={isApplyDisabled}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}

export default Search
